<template>
  <div
    class="silo-card"
    :class="{
      'silo-card--detail': variant === 'detail',
      'silo-card--horizontal': layout === 'horizontal',
      'silo-card--visualization-only': display === 'visualization',
      'silo-card--table-only': display === 'table',
      'silo-card--compact': compact,
    }"
  >
    <!-- Título do Silo -->
    <div v-if="!hideHeader" class="silo-header">
      <h2 class="silo-title">
        {{ siloName }}
      </h2>
    </div>

    <!-- Conteúdo principal com gráficos -->
    <div v-if="showVisualization" class="silo-content">
      <!-- Visualização do Silo -->
      <div class="silo-visualization">
        <div class="silo-container">
          <!-- Escala de altura -->
          <div class="height-scale">
            <span
              v-for="(item, i) in heightScaleItems"
              :key="'h-' + i"
              class="height-scale-number"
              :style="{ top: item.topPercent + '%' }"
            >
              {{ item.label }}
            </span>
          </div>

          <!-- Container do Silo -->
          <div
            class="silo-graphic"
            :class="{ 'silo-graphic--flat-bottom': !hasLowerCone, 'silo-graphic--cone-bottom': hasLowerCone }"
          >
            <!-- Silo vazio (contorno) -->
            <svg
              viewBox="0 0 200 300"
              class="silo-outline"
              preserveAspectRatio="none"
            >
              <!-- Corpo do silo (sin línea superior: no dibujar el borde cono/cilindro) -->
              <path
                :d="siloBodyOutlinePath"
                fill="none"
                stroke="#d1d5db"
                stroke-width="2"
              />
              <!-- Topo triangular (solo lados, sin base para no dibujar la línea cono/cilindro) -->
              <path
                d="M 40 20 L 100 0 L 160 20"
                fill="none"
                stroke="#d1d5db"
                stroke-width="2"
              />
              <!-- Base triangular (só quando o silo tem cone inferior no cadastro) -->
              <polygon
                v-if="hasLowerCone"
                :points="`40,${cylinderBottomSvg} 100,300 160,${cylinderBottomSvg}`"
                fill="none"
                stroke="#d1d5db"
                stroke-width="2"
              />
              <!-- Fundo chato -->
              <line
                v-if="!hasLowerCone"
                x1="40"
                y1="300"
                x2="160"
                y2="300"
                stroke="#d1d5db"
                stroke-width="2"
              />
            </svg>

            <!-- Nível preenchido -->
            <svg
              v-if="(Number(silo.level) || 0) > 0"
              viewBox="0 0 200 300"
              class="silo-filled"
              preserveAspectRatio="none"
            >
              <g v-if="shouldShowCone">
                <polygon
                  :points="coneFillPoints"
                  :fill="siloFillColor"
                />
              </g>

              <g v-if="shouldShowBody">
                <defs>
                  <clipPath :id="`body-clip-${silo.id}`">
                    <rect
                      x="40"
                      :y="bodyClipY"
                      width="120"
                      :height="bodyClipHeight"
                    />
                  </clipPath>
                </defs>
                <rect
                  x="40"
                  y="20"
                  width="120"
                  :height="cylinderHeightSvg"
                  :fill="siloFillColor"
                  :clip-path="`url(#body-clip-${silo.id})`"
                />
              </g>
            </svg>

            <!-- Líneas horizontales cada 0.90 m (por encima del grano/ração) -->
            <svg
              v-if="!hideChapasDisplay"
              viewBox="0 0 200 300"
              class="silo-lines"
              preserveAspectRatio="none"
            >
              <line
                v-for="(y, i) in cylinderHorizontalLineYs"
                :key="`hline-${i}`"
                x1="40"
                :y1="y"
                x2="160"
                :y2="y"
                stroke="#d1d5db"
                stroke-width="1"
              />
            </svg>

            <!-- Líneas que unen el cilindro con la escala de chapas -->
            <svg
              v-if="!hideChapasDisplay"
              viewBox="0 0 200 300"
              class="silo-chapa-guides"
              preserveAspectRatio="none"
            >
              <line
                v-for="item in chapaScaleItems"
                :key="'guide-' + item.number"
                x1="160"
                :y1="item.ySvg"
                x2="200"
                :y2="item.ySvg"
                stroke="#9ca3af"
                stroke-width="1"
              />
            </svg>

            <div class="level-percentage" :style="levelPercentageStyle">
              {{ displayLevel.toFixed(1) }}%
            </div>

            <!-- Escala de chapas a la derecha: 1, 5, 10, 15, ... total -->
            <div v-if="!hideChapasDisplay" class="chapa-scale">
              <span
                v-for="item in chapaScaleItems"
                :key="item.number"
                class="chapa-scale-number"
                :style="{ top: item.topPercent + '%' }"
              >
                {{ item.number }} ch
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela de Informações -->
    <div v-if="showTable" class="silo-table-container">
      <table class="silo-table">
        <tbody>
          <tr v-if="display === 'table'">
            <td class="table-label">Silo</td>
            <td class="table-value">{{ siloName }}</td>
          </tr>
          <tr>
            <td class="table-label">Data-hora</td>
            <td class="table-value">{{ siloDateTime }}</td>
          </tr>
          <tr>
            <td class="table-label">Nível</td>
            <td class="table-value">{{ displayLevel === 0 ? '0' : displayLevel.toFixed(2) }}%</td>
          </tr>
          <tr v-if="!hideChapasDisplay">
            <td class="table-label">Chapas ({{ plateHeightFormatted }})</td>
            <td class="table-value">{{ levelInChapasFormatted }} ({{ levelInMeters}})</td>
          </tr>
          <tr>
            <td class="table-label">Volume</td>
            <td class="table-value">{{ displayVolume === 0 ? '0' : displayVolume.toFixed(3) }} m³</td>
          </tr>
          <tr>
            <td class="table-label">Peso (kg)</td>
            <td class="table-value">{{ formatNumber(displayWeight, 2) }}</td>
          </tr>
          <tr>
            <td class="table-label">Sacas</td>
            <td class="table-value">{{ formatNumber(displayBags, 2) }}</td>
          </tr>
          <tr v-if="showRemainingCapacityRow" class="capacity-remaining-row">
            <td class="table-label">Capacidade Restante</td>
            <td class="table-value">{{ remainingCapacityDisplay }}</td>
          </tr>
          <tr>
            <td class="table-label">Matéria Prima</td>
            <td class="table-value">{{ siloFood }}</td>
          </tr>
          <tr>
            <td class="table-label">Densidade (kg/m³)</td>
            <td class="table-value">{{ formatNumber(silo.density) }}</td>
          </tr>
          <tr v-if="!hideChapasDisplay">
            <td class="table-label">{{ sectionLabel }}</td>
            <td class="table-value">{{ cylinderSectionCount }} ch</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import {
  EMPTY_SILO_LEVEL_THRESHOLD,
  estimateTotalCapacityVolumeM3,
  fillGeometryFromLevelPercent,
  getFlatBottomDimensionsFromCurves,
  getSiloDimensionsFromCurves,
  remainingCapacityTonsFromProfile,
  siloTotalVolumeM3,
} from '../utils/siloLevelToKg';
import { getSiloDisplayWeightAndBags } from '../utils/transformSiloProfileForCard';
import { isChapasDisplayHidden } from '../utils/siloChapasVisibility';

const BODY_TOP_SVG = 20;
const BODY_BOTTOM_SVG = 300;
const BODY_DRAWABLE_HEIGHT = BODY_BOTTOM_SVG - BODY_TOP_SVG;
const DEFAULT_CONE_VISUAL_RATIO = 40 / BODY_DRAWABLE_HEIGHT;
const MIN_CONE_HEIGHT_M = 0.05;

/** Divisões proporcionais ao total de chapas (como a escala de altura: 0%, 25%, 50%, 75%, 100%). */
function getChapaScaleNumbers(total) {
  if (total <= 0) return [];
  if (total <= 4) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const fractions = [0, 0.25, 0.5, 0.75, 1];
  const numbers = fractions.map((f) =>
    Math.max(1, Math.min(total, Math.round(f * (total - 1) + 1)))
  );
  return [...new Set(numbers)];
}

/** Posições (m) das linhas de chapas: divisão uniforme na altura real do cilindro. */
function getChapaLineHeightsM(cylinderHeightM, totalChapas) {
  if (totalChapas <= 0 || cylinderHeightM <= 0) return [];
  const segmentM = cylinderHeightM / totalChapas;
  return Array.from({ length: totalChapas }, (_, i) => (i + 1) * segmentM);
}

/** Converte altura (m) do fundo do silo para % vertical no SVG (0 m = base, total = topo do cilindro). */
function metersToSvgTopPercent(heightM, totalHeightM) {
  if (totalHeightM <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, heightM / totalHeightM));
  const ySvg = BODY_BOTTOM_SVG - ratio * BODY_DRAWABLE_HEIGHT;
  return (ySvg / BODY_BOTTOM_SVG) * 100;
}

export default {
  name: 'SiloCard',
  props: {
    silo: {
      type: Object,
      required: true
    },
    plateHeight: {
      type: Number,
      default: 0.9
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'detail'].includes(v)
    },
    layout: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal'].includes(v)
    },
    showRemainingCapacity: {
      type: Boolean,
      default: false
    },
    display: {
      type: String,
      default: 'full',
      validator: (v) => ['full', 'visualization', 'table'].includes(v)
    },
    compact: {
      type: Boolean,
      default: false,
    },
    farmPk: {
      type: Number,
      default: null,
    }
  },
  computed: {
    showVisualization() {
      return this.display === 'full' || this.display === 'visualization';
    },
    showTable() {
      return this.display === 'full' || this.display === 'table';
    },
    siloDimensions() {
      return getSiloDimensionsFromCurves(this.silo?.curves);
    },
    hasLowerCone() {
      if (this.silo?.hasLowerCone === false) return false;
      if (this.silo?.hasLowerCone === true) return true;
      const dims = this.siloDimensions;
      if (dims) return dims.coneHeight > MIN_CONE_HEIGHT_M;
      return false;
    },
    coneVisualRatio() {
      if (!this.hasLowerCone) return 0;
      const dims = this.siloDimensions;
      if (dims?.totalHeight > 0) {
        return Math.min(1, Math.max(0, dims.coneHeight / dims.totalHeight));
      }
      return DEFAULT_CONE_VISUAL_RATIO;
    },
    coneSvgHeight() {
      return this.coneVisualRatio * BODY_DRAWABLE_HEIGHT;
    },
    cylinderBottomSvg() {
      if (!this.hasLowerCone) return BODY_BOTTOM_SVG;
      return BODY_BOTTOM_SVG - this.coneSvgHeight;
    },
    cylinderHeightSvg() {
      return this.cylinderBottomSvg - BODY_TOP_SVG;
    },
    siloBodyOutlinePath() {
      const bottom = this.cylinderBottomSvg;
      return `M 40 20 L 40 ${bottom} L 160 ${bottom} L 160 20`;
    },
    cylinderHeightM() {
      const dims = this.siloDimensions;
      if (dims) {
        return this.hasLowerCone ? dims.cylinderHeight : dims.totalHeight;
      }
      return this.maxHeight;
    },
    // Altura máxima do silo a partir da curva "Silo dimensions"
    maxHeight() {
      const dims = this.siloDimensions;
      if (dims?.totalHeight) return dims.totalHeight;

      const curve = this.silo.curves?.find(
        (c) => c?.name === 'Silo dimensions' || c?.name === 'silo_dimensions'
      );
      if (curve?.points?.y?.length) {
        const ys = curve.points.y.map(Number).filter(Number.isFinite);
        if (ys.length) {
          return Math.max(...ys) - Math.min(...ys);
        }
      }

      return 24.6;
    },

    // Total de chapas no cilindro — arredonda H/plateHeight (evita fração mínima no topo com ceil)
    totalChapas() {
      const spacingM = Number(this.plateHeight) || 0.9;
      const cylinderHeightM = this.cylinderHeightM;
      if (cylinderHeightM <= 0 || spacingM <= 0) return 0;
      return Math.max(1, Math.round(cylinderHeightM / spacingM));
    },

    // Linhas horizontais entre chapas — espaçamento uniforme até o topo do cilindro
    cylinderHorizontalLineYs() {
      const cylinderHeightM = this.cylinderHeightM;
      const total = this.totalChapas;
      if (total <= 0 || cylinderHeightM <= 0) return [];

      return getChapaLineHeightsM(cylinderHeightM, total).map((h) => {
        const ratio = h / cylinderHeightM;
        return this.cylinderBottomSvg - ratio * this.cylinderHeightSvg;
      });
    },

    heightScaleItems() {
      const max = this.maxHeight;
      if (max <= 0) return [];
      return [1, 0.75, 0.5, 0.25, 0].map((f) => ({
        label: `${(max * f).toFixed(1)} m`,
        topPercent: metersToSvgTopPercent(max * f, max),
      }));
    },

    // Valor de chapa en metros (formato PT-BR) para usar en etiquetas
    plateHeightFormatted() {
      const h = Number(this.plateHeight) || 0.9;
      return h.toFixed(2).replace('.', ',') + ' m';
    },
    // Etiqueta dinámica para secciones (plateHeight del backend, formato PT-BR)
    sectionLabel() {
      return 'Total de chapas';
    },

    cylinderSectionCount() {
      return this.totalChapas;
    },

    // Escala direita: divisões proporcionais ao total de chapas de cada silo
    chapaScaleItems() {
      const total = this.cylinderSectionCount;
      const numbersToShow = new Set(getChapaScaleNumbers(total));

      return this.cylinderHorizontalLineYs
        .map((ySvg, i) => ({ number: i + 1, topPercent: (ySvg / 300) * 100, ySvg }))
        .filter(item => numbersToShow.has(item.number));
    },

    // Mapear serial → sensorCode
    siloSerial() {
      return this.silo.serial || this.silo.sensorCode || 'N/A';
    },
    
    // Mapear last_updated → dateTime y formatear con date-fns
    siloDateTime() {
      const dateValue = this.silo.last_updated || this.silo.dateTime;
      
      if (!dateValue || dateValue === 'N/A') {
        return 'N/A';
      }
      
      try {
        // Intentar parsear y formatear la fecha
        const date = parseISO(dateValue);
        return format(date, 'dd/MM/yyyy HH:mm');
      } catch (error) {
        // Si hay error al parsear, devolver el valor original
        return dateValue;
      }
    },
    
    // Mapear food (pk) → material
    siloFood() {
      return this.silo.food || this.silo.material || 'N/A';
    },
    
    // Nombre del silo
    siloName() {
      const name = this.silo.silo_name || 'Silo sem nome';
      const pk = this.silo.silo ?? this.silo.silo_pk;
      if (pk != null && pk !== '') {
        return `${name} - (${pk})`;
      }
      return name;
    },
    
    // Color del relleno según nivel
    siloFillColor() {
      const level = Number(this.silo.level) || 0;
      
      if (level > 30) {
        return '#a1cd7b'; // Verde - Bom
      } else if (level > 15) {
        return '#e2bf65'; // Amarillo - Medio
      } else if (level > 0) {
        return '#c25757'; // Rojo - Ruim
      } else {
        return '#adadad'; // Gris - Sin datos
      }
    },
    
    /**
     * Level is a volume ratio (V_filled / V_total). Convert to cone/cylinder fill
     * ratios before drawing — do not treat level as linear height %.
     */
    fillGeometry() {
      const level = Number(this.silo.level) || 0;
      if (level <= 0) {
        return {
          fillHeightM: 0,
          coneFillRatio: 0,
          cylinderFillRatio: 0,
          inConeOnly: true,
        };
      }

      const dims = this.siloDimensions;

      if (!this.hasLowerCone) {
        const flatDims = getFlatBottomDimensionsFromCurves(this.silo?.curves);
        if (flatDims) {
          return fillGeometryFromLevelPercent(level, flatDims);
        }
        const ratio = Math.min(1, level / 100);
        return {
          fillHeightM: ratio * this.cylinderHeightM,
          coneFillRatio: 0,
          cylinderFillRatio: ratio,
          inConeOnly: false,
        };
      }

      if (dims) {
        return fillGeometryFromLevelPercent(level, dims);
      }

      // Sem dimensões: aproximação linear (cone visual ~12% da altura).
      const coneShare = DEFAULT_CONE_VISUAL_RATIO;
      if (level / 100 <= coneShare) {
        const coneFillRatio = level / 100 / coneShare;
        return {
          fillHeightM: coneFillRatio * coneShare * this.maxHeight,
          coneFillRatio,
          cylinderFillRatio: 0,
          inConeOnly: true,
        };
      }
      const cylinderFillRatio = (level / 100 - coneShare) / (1 - coneShare);
      return {
        fillHeightM: (coneShare + cylinderFillRatio * (1 - coneShare)) * this.maxHeight,
        coneFillRatio: 1,
        cylinderFillRatio,
        inConeOnly: false,
      };
    },

    shouldShowCone() {
      if (!this.hasLowerCone) return false;
      return this.fillGeometry.coneFillRatio > 0;
    },

    shouldShowBody() {
      return this.fillGeometry.cylinderFillRatio > 0;
    },

    coneFillPoints() {
      if (!this.hasLowerCone) return '';

      const { coneFillRatio } = this.fillGeometry;
      if (coneFillRatio <= 0) return '';

      const coneTopBase = this.cylinderBottomSvg;

      if (coneFillRatio >= 1) {
        return `40,${coneTopBase} 100,300 160,${coneTopBase}`;
      }

      const filledHeight = this.coneSvgHeight * coneFillRatio;
      const coneTopY = BODY_BOTTOM_SVG - filledHeight;
      const heightFromBase = coneTopY - coneTopBase;
      const widthAtLevel = Math.max(0, 120 * (1 - heightFromBase / this.coneSvgHeight));
      const minWidth = coneFillRatio < 0.01 ? Math.max(0.5, widthAtLevel) : Math.max(1, widthAtLevel);
      const leftX = 100 - (minWidth / 2);
      const rightX = 100 + (minWidth / 2);

      return `100,300 ${leftX},${coneTopY} ${rightX},${coneTopY}`;
    },

    bodyClipY() {
      const { cylinderFillRatio } = this.fillGeometry;
      if (cylinderFillRatio <= 0) return this.cylinderBottomSvg;
      return this.cylinderBottomSvg - cylinderFillRatio * this.cylinderHeightSvg;
    },

    bodyClipHeight() {
      const { cylinderFillRatio } = this.fillGeometry;
      if (cylinderFillRatio <= 0) return 0;
      return cylinderFillRatio * this.cylinderHeightSvg;
    },
    visualFillHeightRatio() {
      const totalH = this.siloDimensions?.totalHeight || this.maxHeight;
      if (totalH <= 0) return 0;
      const { fillHeightM } = this.fillGeometry;
      return Math.min(1, Math.max(0, fillHeightM / totalH));
    },
    levelPercentageStyle() {
      const ratio = this.visualFillHeightRatio;
      if (ratio <= 0.01) return {};
      const fillTopY = BODY_BOTTOM_SVG - ratio * BODY_DRAWABLE_HEIGHT;
      const centerY = (fillTopY + BODY_BOTTOM_SVG) / 2;
      const topPercent = (centerY / BODY_BOTTOM_SVG) * 100;
      return {
        top: `${Math.min(92, Math.max(8, topPercent))}%`,
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
      };
    },
    displayLevel() {
      const level = Number(this.silo.level) || 0;
      return level < 1 ? 0 : level;
    },

    filledChapas() {
      const total = this.cylinderSectionCount;
      if (total <= 0) return 0;

      const { cylinderFillRatio } = this.fillGeometry;
      if (cylinderFillRatio <= 0) return 0;

      const plateH = Number(this.plateHeight) || 0.9;
      const cylinderFillHeightM = cylinderFillRatio * this.cylinderHeightM;
      const chapas = cylinderFillHeightM / plateH;
      return Math.min(total, Math.max(0, Math.round(chapas)));
    },
    levelInMeters() {
      const plateH = Number(this.plateHeight) || 0.9;
      const meters = this.filledChapas * plateH;
      return `${meters.toFixed(2).replace('.', ',')} metros`;
    },

    // Chapas de ração (entero) y total de chapas: "valor / total chapas"
    levelInChapasFormatted() {
      const total = this.cylinderSectionCount;
      return `${this.filledChapas} ch / ${total} ch chapas`;
    },
    displayVolume() {
      const level = Number(this.silo.level) || 0;
      return level < 0.1 ? 0 : (Number(this.silo.volume) || 0);
    },
    displayWeight() {
      return getSiloDisplayWeightAndBags(this.silo).weight;
    },
    displayBags() {
      return getSiloDisplayWeightAndBags(this.silo).bags;
    },

    companySettings() {
      return this.$store?.getters?.getCompanySettings;
    },

    showRemainingCapacityRow() {
      return this.showRemainingCapacity || this.showSiloHome;
    },

    /**
     * Gate de visibilidade do "Capacidade Restante" — mesma condição usada no resto da home.
     */
    showSiloHome() {
      return this.companySettings?.settings?.client_frontend?.show_silo_home ?? false;
    },

    hideChapasDisplay() {
      return isChapasDisplayHidden(this.companySettings, this.farmPk);
    },

    /**
     * Capacidade volumétrica máxima do silo (m³), derivada da curva "Silo dimensions"
     * ou estimada a partir do nível/volume do perfil atual.
     */
    totalCapacityVolumeM3() {
      if (this.silo?.useLevelMetrics) {
        const dims = getSiloDimensionsFromCurves(this.silo?.curves);
        if (dims) {
          const total = siloTotalVolumeM3(dims);
          return Number.isFinite(total) && total > 0 ? total : null;
        }
      }

      return estimateTotalCapacityVolumeM3({
        curves: this.silo?.curves,
        level: this.silo?.level,
        volume: this.silo?.volume,
      });
    },

    /**
     * Volume atual ocupado em m³ (clamp em 0 e na capacidade máxima quando aplicável).
     */
    currentVolumeM3() {
      const level = Number(this.silo?.level) || 0;
      if (level < EMPTY_SILO_LEVEL_THRESHOLD) return 0;
      const v = Number(this.silo?.volume) || 0;
      return v > 0 ? v : 0;
    },

    /**
     * Volume restante (m³): capacidade total − volume atual, com piso em 0
     * para o caso de silos sobre-preenchidos (level > 100%).
     */
    remainingVolumeM3() {
      const total = this.totalCapacityVolumeM3;
      if (total == null) return null;
      const remaining = total - this.currentVolumeM3;
      return remaining > 0 ? remaining : 0;
    },

    remainingCapacityTons() {
      return remainingCapacityTonsFromProfile({
        curves: this.silo?.curves,
        level: this.silo?.level,
        volume: this.silo?.volume,
        density: this.silo?.density,
        weight: this.silo?.weight,
        useLevel: this.silo?.useLevelMetrics === true,
        informedMaxWeightKg: this.silo?.informedMaxWeight,
      });
    },

    /**
     * Texto pronto para exibição: "X,XX t" ou "N/A" quando os dados são insuficientes.
     */
    remainingCapacityDisplay() {
      const tons = this.remainingCapacityTons;
      if (tons == null) return 'N/A';
      const decimals = tons >= 100 ? 1 : 2;
      const formatted = tons.toLocaleString('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      return `${formatted} t`;
    }
  },
  methods: {
    formatNumber(value, fractionDigits = null) {
      if (fractionDigits == null) {
        return Number(value).toLocaleString('pt-BR');
      }
      return Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    }
  }
};
</script>

<style scoped>
.silo-card {
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
  height: 100%;
  margin: 0 auto;
}

.silo-header {
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 8px;
}

.silo-title {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  color: #1f2937;
  margin: 0;
}

.silo-content {
  padding: 12px 8px;
  display: flex;
  justify-content: center;
}

.silo-visualization {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
}

.silo-container {
  position: relative;
  width: 148px;
  max-width: 100%;
  margin: 0 auto;
}

.height-scale {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 30px;
  font-size: 9px;
  color: #4b5563;
  pointer-events: none;
}

.height-scale-number {
  position: absolute;
  right: 3px;
  transform: translateY(-50%);
  line-height: 1;
  white-space: nowrap;
}

.height-scale-number:first-child {
  transform: translateY(-100%);
}

.silo-graphic {
  margin-left: 34px;
  position: relative;
  width: 90px;
  height: 155px;
}

.chapa-scale {
  position: absolute;
  left: 100%;
  top: 0;
  bottom: 0;
  width: 28px;
  margin-left: 2px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
  pointer-events: none;
}

.chapa-scale-number {
  position: absolute;
  left: 0;
  transform: translateY(-50%);
  line-height: 1;
  white-space: nowrap;
}

.chapa-scale-number:last-child {
  transform: translateY(-100%);
}

.silo-outline,
.silo-filled,
.silo-lines,
.silo-chapa-guides {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.silo-lines,
.silo-chapa-guides {
  pointer-events: none;
}

.level-percentage {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  padding: 3px 9px;
  border-radius: 999px;
  color: #1b4238;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #b8d4a0;
  box-shadow: 0 1px 4px rgba(27, 66, 56, 0.14);
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
}

.silo-table-container {
  padding: 0 6px 6px 6px;
}

.silo-table {
  width: 100%;
  border-collapse: collapse;
}

.silo-table tr {
  border-top: 1px solid #e5e7eb;
}

.table-label {
  padding: 6px 7px;
  font-size: 10.5px;
  color: #4b5563;
  background-color: #f9fafb;
  font-weight: 500;
}

.table-value {
  padding: 6px 7px;
  font-size: 10.5px;
  color: #1f2937;
  word-break: break-word;
  hyphens: auto;
}

.capacity-remaining-row .table-label {
  color: #1F7A1F;
  font-weight: 600;
}

.capacity-remaining-row .table-value {
  color: #1F7A1F;
  font-weight: 600;
}

.silo-card--detail .silo-container {
  width: 172px;
}

.silo-card--detail .silo-graphic {
  width: 104px;
  height: 178px;
}

.silo-card--detail .level-percentage {
  font-size: 17px;
}

.silo-card--detail.silo-card {
  box-shadow: none;
  border: none;
  height: auto;
}

.silo-card--detail .silo-content {
  padding: 8px 4px 12px;
}

.silo-card--detail .silo-table-container {
  padding: 0 4px 4px;
}

.silo-card--visualization-only.silo-card {
  box-shadow: none;
  border: none;
  height: auto;
}

.silo-card--visualization-only .silo-content {
  padding: 4px 8px;
}

.silo-card--table-only.silo-card {
  box-shadow: none;
  border: none;
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
}

.silo-card--table-only .silo-table tr {
  border-top: 1px solid #ddd;
}

.silo-card--table-only .table-label,
.silo-card--table-only .table-value {
  border: 1px solid #ddd;
  font-size: 12px;
  padding: 5px 7px;
}

.silo-card--table-only .table-label {
  width: 40%;
  font-weight: 600;
}

.silo-card--table-only .silo-table tr:nth-child(even) .table-value,
.silo-card--table-only .silo-table tr:nth-child(even) .table-label {
  background-color: #f2f2f2;
}

.silo-card--table-only .table-label {
  background-color: #f8f9fa;
}

.silo-card--horizontal {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  width: 100%;
}

.silo-card--horizontal .silo-content {
  grid-column: 1;
  padding: 8px;
}

.silo-card--horizontal .silo-table-container {
  grid-column: 2;
  padding: 8px 8px 8px 0;
  align-self: stretch;
}

.silo-card--detail.silo-card--horizontal .silo-container {
  width: 200px;
}

.silo-card--detail.silo-card--horizontal .silo-graphic {
  width: 122px;
  height: 210px;
}

.silo-card--detail.silo-card--horizontal .level-percentage {
  font-size: 18px;
}

.silo-card--detail.silo-card--horizontal .table-label,
.silo-card--detail.silo-card--horizontal .table-value {
  font-size: 11px;
  padding: 5px 8px;
}

@media screen and (max-width: 640px) {
  .silo-card--horizontal {
    grid-template-columns: 1fr;
  }

  .silo-card--horizontal .silo-content,
  .silo-card--horizontal .silo-table-container {
    grid-column: 1;
    padding: 8px;
  }
}

.silo-card--compact {
  max-width: 200px;
  width: 100%;
}

.silo-card--compact .silo-header {
  padding: 8px 6px;
}

.silo-card--compact .silo-title {
  font-size: 11px;
}

.silo-card--compact .silo-content {
  padding: 8px 4px;
}

.silo-card--compact .silo-container {
  width: 118px;
}

.silo-card--compact .silo-graphic {
  width: 72px;
  height: 124px;
  margin-left: 28px;
}

.silo-card--compact .height-scale {
  font-size: 7px;
}

.silo-card--compact .chapa-scale {
  width: 22px;
  font-size: 9px;
}

.silo-card--compact .level-percentage {
  font-size: 11px;
  padding: 2px 6px;
}

.silo-card--compact .silo-table-container {
  padding: 0 4px 4px;
}

.silo-card--compact .table-label,
.silo-card--compact .table-value {
  font-size: 9px;
  padding: 4px 5px;
}
</style>

